import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import Slice9Button from "../../../../../lib/html5-software-library/src/ranj/components/scale9/Slice9Button";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import PullContentManager from "../../../ngt/pull/PullContentManager";
import NPCModel from "../../../ngt/scenario/NPCModel";
import ScenarioLibrary from "../../../ngt/scenario/ScenarioLibrary";
import CharacterLibrary from "../../characters/model/CharacterLibrary";
import CharacterModel from "../../characters/model/CharacterModel";
import Conversation from "../../conversation/model/Conversation";
import ProgressModel from "../../progress/model/ProgressModel";
import SaveGameEvent from "../../savegame/event/SaveGameEvent";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import MovieClipUtil from "../../util/MovieClipUtil";
import PromiseUtil from "../../util/PromiseUtil";
import ConversationResultEvent from "../event/ConversationResultEvent";

export default class ConversationResultScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {CharacterLibrary} characterLibrary 
     * @param {PullContentManager} pullContentManager
     * @param {AssetLibrary} assetLibrary 
     * @param {ScenarioLibrary} scenarioLibrary
     * @param {NPCModel} npcModel
     * @param {SoundManager} soundManager
     * @param {ProgressModel} progressModel
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, characterLibrary, pullContentManager, assetLibrary, scenarioLibrary, npcModel, soundManager, progressModel) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._characterLibrary = characterLibrary;
        this._pullContentManager = pullContentManager;
        this._assetLibrary = assetLibrary;
        this._scenarioLibrary = scenarioLibrary;
        this._npcModel = npcModel;
        this._soundManager = soundManager;
        this._progressModel = progressModel;

        /** @type {Conversation} */
        this._conversation = this.getCustomData().conversation;
        // Get active characters.
        const definedNpcs = this._npcModel.getNPCs();
        /** @type {CharacterModel[]} */
        this._characters = definedNpcs.map(npc => {
            const characterId = npc.getNpcID()?.trim();
            return this._characterLibrary.getCharacterById(characterId);
        });
    }

    async initialize() {
        super.initialize();
        this.preventClickThrough();

        this._persistProgress();

        this._initializeBackgroundImage();

        this._target.titleTextField.text = this._languagePack.getText(`conversationResult.title`);

        this._continueButton = this.createSlice9Button(this._target.continueButton, this._handleContinueButtonClick.bind(this), this._languagePack.getText(`conversationResult.continueButtonLabel`), SoundType.BUTTON_CLICK, Slice9Button.HORIZONTAL);
        this._continueButton.setVisible(false);

        this._populatePanels();
        await this._showTrackerDifferences();

        this._continueButton.setVisible(true);

        this.dispatch(SaveGameEvent.SHOW_PENDING_FEEDBACK);
    }

    _persistProgress() {
        const currentScenario = this._scenarioLibrary.getCurrentScenario();
        this._progressModel.markScenarioCompleted(currentScenario.id);
        this.dispatch(SaveGameEvent.SAVE);
    }

    _initializeBackgroundImage() {
        this._target.backgroundImageContainer.gotoAndStop(0); // Ensures that the children are populated.
        this._target.backgroundImageContainer.removeAllChildren();

        const imageId = this._scenarioLibrary.getCurrentScenario().resultBackgroundImageId;
        const image = this._assetLibrary.getAsset(imageId);
        if (!image) {
            console.warn(`Image with id ${imageId} does not exist. Ignoring request...`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        this._target.backgroundImageContainer.addChild(bitmap);
    }

    async _populatePanels() {
        for (let i = 0; i < this._characters.length; i++) {
            const character = this._characters[i];
            const panel = this._target[`characterTrackerPanel${i + 1}`];

            this._populateCharacterPanel(character, panel);
        }

        for (let i = this._characters.length; i < 100; i++) {
            const panel = this._target[`characterTrackerPanel${i + 1}`];
            if (!panel) {
                break;
            }

            panel.visible = false;
        }

    }

    _populateCharacterPanel(character, panel) {
        panel.profile.nameTextField.text = character.name;
        panel.trustLevelName.text = this._languagePack.getText(`conversationResult.trustLevelName`);
        panel.groupLevelName.text = this._languagePack.getText(`conversationResult.groupLevelName`);

        const startingTrackers = this._conversation.getStartingTrackersForCharacter(character.id);

        const startingTrust = startingTrackers.find(tracker => tracker.id === 'trust')?.value || 0;
        panel.trustMeter.gotoAndStop(startingTrust * ConversationResultScreenMediator.TRACKER_METER_STEPS_TO_FRAMES);

        const startingGroupSpirit = startingTrackers.find(tracker => tracker.id === 'groupSpirit')?.value || 0;
        panel.groupMeter.gotoAndStop(startingGroupSpirit * ConversationResultScreenMediator.TRACKER_METER_STEPS_TO_FRAMES);

        this._setImage(`${character.id}NeutralEmotion`, panel.profile.charImageContainer);
    }

    async _showTrackerDifferences() {
        await PromiseUtil.delay(650);

        for (let i = 0; i < this._characters.length; i++) {
            const character = this._characters[i];
            const panel = this._target[`characterTrackerPanel${i + 1}`];

            await this._showTrackerDifferencesForCharacter(character, panel);
        }
    }

    async _showTrackerDifferencesForCharacter(character, panel) {
        const startingTrackers = this._conversation.getStartingTrackersForCharacter(character.id);
        const endingTrackers = this._conversation.getEndingTrackersForCharacter(character.id);

        const startingTrust = startingTrackers.find(tracker => tracker.id === 'trust')?.value || 0;
        const endingTrust = endingTrackers.find(tracker => tracker.id === 'trust')?.value || 0;
        if (startingTrust !== endingTrust) {
            await PromiseUtil.delay(150);
            this._playTrackerMeterSound(startingTrust, endingTrust);
            const trustMeterTween = MovieClipUtil.animateToFrame(panel.trustMeter, endingTrust * ConversationResultScreenMediator.TRACKER_METER_STEPS_TO_FRAMES, 450, createjs.Ease.sineInOut);
            await trustMeterTween.toPromise();
        }

        const startingGroupSpirit = startingTrackers.find(tracker => tracker.id === 'groupSpirit')?.value || 0;
        const endingGroupSpirit = endingTrackers.find(tracker => tracker.id === 'groupSpirit')?.value || 0;
        if (startingGroupSpirit !== endingGroupSpirit) {
            await PromiseUtil.delay(150);
            this._playTrackerMeterSound(startingGroupSpirit, endingGroupSpirit);
            const groupSpiritMeterTween = MovieClipUtil.animateToFrame(panel.groupMeter, endingGroupSpirit * ConversationResultScreenMediator.TRACKER_METER_STEPS_TO_FRAMES, 450, createjs.Ease.sineInOut);
            await groupSpiritMeterTween.toPromise();
        }
    }

    _playTrackerMeterSound(startValue, endValue) {
        if (startValue < endValue) {
            this._soundManager.playSound(SoundType.TRACKER_BAR_INCREASE);
        } else if (startValue > endValue) {
            this._soundManager.playSound(SoundType.TRACKER_BAR_DECREASE);
        }
    }

    _setImage(imageId, imageContainer) {
        imageContainer.removeAllChildren();

        // Get asset, convert to bitmap, set anchor to center, add as child
        const image = this._assetLibrary.getAsset(imageId);

        if (!image) {
            console.warn(`Image is does not exist. You tried: ${imageId}`);
            return;
        }

        const bitmap = new createjs.Bitmap(image);
        DisplayObjectUtil.anchorDisplayObject(bitmap, Anchor.C);
        imageContainer.addChild(bitmap);
    }

    _handleContinueButtonClick() {
        this.disableInteraction();

        const conversationResultEvent = new ConversationResultEvent(ConversationResultEvent.FINISH, this._conversation);
        this.dispatch(conversationResultEvent.type, { data: conversationResultEvent });
    }
}

ConversationResultScreenMediator.TRACKER_METER_STEPS_TO_FRAMES = 20; // There are 5 steps in the tracker meter, so 100 / 5 = 20 frames.